import type { RealtimeChannel, SupabaseClient } from "@supabase/supabase-js";

type Subscription = {
  listeners: Set<() => void>;
  channels: RealtimeChannel[];
  closing?: Promise<void>;
};
const subscriptions = new WeakMap<SupabaseClient, Map<string, Subscription>>();
let generation = 0;

/**
 * Supabase reuses channels by topic. React effect replay/token refresh must
 * share listeners, not add postgres callbacks to an already subscribed channel.
 */
export function subscribeHeaderBadges(
  client: SupabaseClient, userId: string, refresh: () => void,
): () => void {
  const users = subscriptions.get(client) ?? new Map<string, Subscription>();
  subscriptions.set(client, users);
  let entry = users.get(userId);
  if (entry?.closing) {
    let cancelled = false;
    let stop: (() => void) | undefined;
    void entry.closing.then(() => {
      if (!cancelled) stop = subscribeHeaderBadges(client, userId, refresh);
    });
    return () => { cancelled = true; stop?.(); };
  }
  if (!entry) {
    entry = { listeners: new Set(), channels: [] };
    const listeners = entry.listeners;
    const notify = () => { for (const listener of listeners) listener(); };
    // The database topic is instance-owned. The broadcast topic must remain
    // exactly the server's per-user inbox topic.
    const rows = client.channel(`header-badges-${userId}-${++generation}`);
    const inbox = client.channel(`bac:user:${userId}`);
    entry.channels = [rows, inbox];
    try {
      rows.on("postgres_changes", { event: "*", schema: "public", table: "messages" }, notify)
        .on("postgres_changes", {
          event: "*", schema: "public", table: "notifications", filter: `user_id=eq.${userId}`,
        }, notify).subscribe();
      inbox.on("broadcast", { event: "inbox" }, notify).subscribe();
    } catch (error) {
      for (const channel of entry.channels)
        void Promise.resolve().then(() => client.removeChannel(channel)).catch(() => {});
      throw error;
    }
    users.set(userId, entry);
  }
  const owned = entry;
  // Each subscription owns a distinct callback, even if two callers supplied
  // the same refresh function.
  const listener = () => refresh();
  owned.listeners.add(listener);
  return () => {
    owned.listeners.delete(listener);
    // StrictMode and dependency changes replay cleanup/setup in the same turn.
    // Reuse the live subscription rather than racing asynchronous removal.
    queueMicrotask(() => {
      if (owned.listeners.size || owned.closing) return;
      owned.closing = Promise.allSettled(owned.channels.map(channel =>
        Promise.resolve().then(() => client.removeChannel(channel)))).then(() => {
          if (users.get(userId) === owned) users.delete(userId);
        });
    });
  };
}
